import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../shared/Toast'

/**
 * Shared "email this to…" modal.
 * Pre-fills the signed-in user's address; `buildPayload` supplies the body.
 */
export default function EmailModal({ title, description, buildPayload, onClose }) {
  const { profile, user } = useAuth()
  const toast = useToast()
  const [to, setTo] = useState(profile?.email || user?.email || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function send(e) {
    e.preventDefault()
    setErr('')
    if (!/^\S+@\S+\.\S+$/.test(to.trim())) {
      setErr('Enter a valid email address.')
      return
    }
    setBusy(true)
    const { data, error } = await supabase.functions.invoke('send-care-plan-email', {
      body: { to: to.trim(), ...buildPayload() },
    })
    setBusy(false)

    // supabase-js surfaces non-2xx as `error`; the function's JSON body carries
    // the human-readable reason, so prefer that when present.
    if (error || data?.error) {
      setErr(data?.error || error?.message || 'Could not send that email.')
      return
    }
    toast.success(`Sent to ${to.trim()}`)
    onClose()
  }

  return (
    <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel">
        <form className="stack" onSubmit={send}>
          <div className="spread">
            <h2 className="title" style={{ fontSize: 22 }}>{title}</h2>
            <button
              type="button" onClick={onClose} aria-label="Close"
              style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
            >
              ×
            </button>
          </div>

          {description && <p className="muted" style={{ fontSize: 14, lineHeight: 1.55 }}>{description}</p>}

          <div className="field">
            <label className="label" htmlFor="email-to">Send to</label>
            <input
              id="email-to" type="email" className="input" autoComplete="email"
              value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@email.com"
            />
          </div>

          {err && <div className="error-text">{err}</div>}

          <button type="submit" className="btn btn-block btn-lg" disabled={busy}>
            {busy ? <span className="spinner" /> : 'Send email'}
          </button>
        </form>
      </div>
    </div>
  )
}
